import { useState, useRef, useEffect, useMemo, RefObject } from "react";

interface UseTimelineCursorProps {
  svgRef: RefObject<SVGSVGElement>;
  scaledWidth: number;
  min: number;
  max: number;
  selectedTime: number | null;
  /**
   * Draw the playback cursor at THIS instant instead of `selectedTime`.
   *
   * For a stack of timelines sharing one playhead: a row that is not the active
   * one has its own cursor store, seeded once and never advanced, so its own
   * `selectedTime` would render a playhead frozen somewhere unrelated. Only the
   * drawn position changes — clicks, seeks and locking still use the real cursor.
   */
  playbackTimeOverride?: number | null;
  lastOrigin?: string;
  onTimeSelect?: (time: number, origin: "internal" | "external") => void;
  onTrackUnlock?: () => void;
}

export const useTimelineCursor = ({
  svgRef,
  scaledWidth,
  min,
  max,
  selectedTime,
  playbackTimeOverride = null,
  lastOrigin,
  onTimeSelect,
  onTrackUnlock,
}: UseTimelineCursorProps) => {
  // Hover cursor: only set by mouse move, cleared on mouse leave
  const [hoverCursorX, setHoverCursorX] = useState<number | null>(null);
  const [lockedCursorTime, setLockedCursorTime] = useState<number | null>(null);
  const cursorRafRef = useRef<number | null>(null);
  const lastCursorXRef = useRef<number | null>(null);

  /**
   * Convert timestamp to X coordinate
   */
  const timeToX = (timestamp: number) => {
    if (max === min) return 0;
    return ((timestamp - min) / (max - min)) * scaledWidth;
  };

  /**
   * Convert X coordinate to timestamp
   */
  const xToTime = (x: number) => {
    if (scaledWidth === 0) return min;
    return min + (x / scaledWidth) * (max - min);
  };

  /**
   * Playback cursor X: derived from selectedTime (always tracks current position)
   */
  const playbackCursorX = useMemo(() => {
    const at = playbackTimeOverride ?? selectedTime;
    if (at === null) return null;
    const x = timeToX(at);
    return isNaN(x) ? null : x;
  }, [selectedTime, playbackTimeOverride, min, max, scaledWidth]);

  /**
   * Get locked cursor X position
   */
  const lockedCursorX = (() => {
    if (lockedCursorTime === null) return null;
    const x = timeToX(lockedCursorTime);
    return isNaN(x) ? null : x;
  })();

  /**
   * Handle SVG mouse move with RAF optimization
   */
  const handleSVGMouseMove = (e: React.MouseEvent) => {
    const svg = svgRef.current;
    if (!svg) return;

    const rect = svg.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * scaledWidth;

    lastCursorXRef.current = isNaN(x) ? null : x;

    if (cursorRafRef.current !== null) return;

    cursorRafRef.current = requestAnimationFrame(() => {
      cursorRafRef.current = null;
      if (lastCursorXRef.current !== null) {
        setHoverCursorX(lastCursorXRef.current);
      }
    });
  };

  /**
   * Handle SVG click to select and freeze cursor
   */
  const handleSVGClick = (e: React.MouseEvent) => {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return;

    const x = ((e.clientX - rect.left) / rect.width) * scaledWidth;
    const timestamp = xToTime(x);

    onTimeSelect?.(timestamp, "internal");
    // Always freeze at the clicked position (double-click to unfreeze)
    setLockedCursorTime(timestamp);
  };

  /**
   * Handle SVG double-click to unlock cursor and unlock track
   */
  const handleSVGDoubleClick = (e: React.MouseEvent) => {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return;

    const x = ((e.clientX - rect.left) / rect.width) * scaledWidth;
    const timestamp = xToTime(x);

    onTimeSelect?.(timestamp, "internal");
    setLockedCursorTime(null);
    onTrackUnlock?.();
  };

  /**
   * Toggle cursor lock/unlock with keyboard shortcut (Cmd/Ctrl+K)
   */
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.code === "KeyK")) {
        e.preventDefault();

        if (lockedCursorTime === null && hoverCursorX !== null) {
          const timestamp = xToTime(hoverCursorX);
          setLockedCursorTime(timestamp);
        } else {
          setLockedCursorTime(null);
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [lockedCursorTime, hoverCursorX, min, max, scaledWidth]);

  /**
   * Handle SVG mouse leave - hide hover cursor
   */
  const handleSVGMouseLeave = () => {
    setHoverCursorX(null);
  };

  return {
    cursorX: hoverCursorX,
    playbackCursorX,
    lockedCursorX,
    lockedCursorTime,
    setLockedCursorTime,
    handleSVGMouseMove,
    handleSVGMouseLeave,
    handleSVGClick,
    handleSVGDoubleClick,
    isLocked: lockedCursorTime !== null,
  };
};
